import { useLocation } from 'react-router-dom';
import { LogOut, Menu, Moon, Sun } from 'lucide-react';

import { useAuth } from '../hooks/use-auth';
import { useTheme } from '../theme/theme-context';

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Overview',
  '/users': 'Users',
  '/content': 'Content',
  '/channels': 'Channels',
  '/alerts': 'Alerts',
  '/notifications': 'Notifications',
  '/mobile': 'Mobile',
  '/analytics': 'Analytics',
  '/errors': 'Errors',
  '/infra': 'Infra',
  '/costs': 'Costs',
  '/audit': 'Audit',
};

function deriveBreadcrumb(pathname: string): string[] {
  // Map the first segment to a label, additional segments stay as-is
  // with first-letter capitalised. Good enough for the v1 sidebar
  // structure; sub-pages are tabs not breadcrumbs.
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return ['Overview'];
  const head = `/${parts[0]}`;
  const label = ROUTE_LABELS[head] ?? capitalise(parts[0]);
  const rest = parts.slice(1).map(capitalise);
  return [label, ...rest];
}

function capitalise(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

export function Topbar({ onMenu }: { readonly onMenu?: () => void }) {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const { mode, toggle } = useTheme();
  const crumbs = deriveBreadcrumb(pathname);

  return (
    <header className="nw-topbar">
      <button
        type="button"
        className="nw-topbar__menu-btn"
        onClick={onMenu}
        aria-label="Open navigation menu"
      >
        <Menu size={20} />
      </button>
      <nav className="nw-topbar__breadcrumb" aria-label="Breadcrumb">
        {crumbs.map((c, i) => (
          <span key={c}>
            {i === 0 ? <b>{c}</b> : (
              <>
                <span className="nw-topbar__breadcrumb-sep">›</span>
                {c}
              </>
            )}
          </span>
        ))}
      </nav>
      <div className="nw-topbar__actions">
        <button
          type="button"
          className="nw-topbar__icon-btn"
          onClick={toggle}
          aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={mode === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <span className="nw-topbar__email">{user?.email}</span>
        <button
          type="button"
          className="nw-topbar__icon-btn"
          onClick={() => void signOut()}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={18} />
        </button>
      </div>
    </header>
  );
}
